import CT from '@marketplace-ai/tags';
import { useLayoutEffect, useRef } from 'react';
import { UserContextV2 } from '~/app';

const TAGS = CT.delegation;

/**
 * [MCV-1752] The banner's rendered height, published on <html> while it is shown. The hamburger
 * and the navigation drawer (MobileMenu), the alerts popover (Notifications) and the in-app FAQ
 * tree (faqLayout) add `var(--impersonation-banner-height,0px)` because they are positioned or
 * sized against the top bar, which now starts below the banner. The drawer starts under the banner
 * so that Exit Impersonation stays visible and clickable while the navigation is open.
 */
const BANNER_HEIGHT_VAR = '--impersonation-banner-height';

/**
 * Impersonation Banner
 *
 * Shows when employer is impersonating a provider
 * HLX-specific feature
 *
 * Renders as SignedInShell's first row in normal flow, never fixed over the page, so the top bar
 * starts right under it and the document keeps no scroll range. It is a full-width row above the
 * sidebar, so its Exit button sits at the banner's own px-4 inset, not past the sidebar's edge,
 * with the message 12px after it.
 *
 * `relative` only gives its shadow a paint order: a static row is painted before the top bar below
 * it, whose background buries the shadow. It deliberately has no z-index, so it stays under every
 * overlay (the lowest dialogs are z-10, the alerts popover and the drawer z-50).
 */
export const ImpersonationBanner = () => {
  const actorRef = UserContextV2.useActorRef();
  const isImpersonating = UserContextV2.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );
  const providerName = UserContextV2.useSelector(
    (state) => state.context.impersonatingProviderName ?? 'Unknown Provider'
  );

  const bannerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const banner = bannerRef.current;
    if (!isImpersonating || !banner) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(BANNER_HEIGHT_VAR, `${banner.getBoundingClientRect().height}px`);
    publish();
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(publish);
    observer?.observe(banner);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, [isImpersonating]);

  if (!isImpersonating) return null;

  return (
    <div
      ref={bannerRef}
      data-cy={TAGS.banner}
      className="relative shrink-0 bg-warning-400 text-white px-4 py-0 shadow-lg flex items-center gap-3"
    >
      {/* [MCV-1763] Exit comes first, at the left edge, so it is easy to find on a wide screen. */}
      <button
        data-cy={TAGS.exitBtn}
        onClick={() => {
          actorRef.send({ type: 'ON_END_IMPERSONATION' });
        }}
        className="shrink-0 bg-white/20 hover:bg-white/30 px-4 py-2 rounded font-medium transition-colors"
      >
        Exit Impersonation
      </button>
      <div className="flex min-w-0 items-center gap-3">
        <div className="min-w-0 text-left break-words">
          <span className="font-semibold">Impersonating Provider:</span>{' '}
          <span data-cy={TAGS.providerName} className="font-bold">
            {providerName}
          </span>
        </div>
      </div>
    </div>
  );
};
